// ---------------------------------------------------------------------------
// The words one player has offered in one letters round, while open answering
// is on.
//
// The server keeps no list of this. That is deliberate and it is not an
// oversight to work around: a record of everything somebody tried is exactly
// the kind of thing a sanitization bug hands to the whole room, which is why
// drafts live outside the serialized state too. So the list belongs to the
// device that typed it, and it lasts as long as the tab does. A reload gets the
// answer back from the server and nothing else, and `restoreAnswer` is how that
// says so rather than looking like the list was eaten.
//
// One row is the player's answer, and the server decides which: the longest
// valid word offered, ties going to the LATEST one — offering MINT and then
// TINT leaves you on TINT. This file said "the earlier one" for as long as that
// was the rule, and following the comment rather than the flag would now paint
// the answer marker on the wrong row. Nothing here re-derives the rule at all:
// `adopted` on a ruling is the server saying "this one is yours now", and the
// only job here is to make sure exactly one row wears it.
//
// A REPEAT IS A REAL OFFER. NOSE, then SEND, then NOSE again lands on NOSE,
// because the latest of two equally long words wins — so a client that refused
// the second NOSE was not saving a round trip, it was refusing the player their
// answer back. The list still holds one row per word: two rows for one string
// would be two rows racing for the answer marker, and only one of them could
// ever be right. So a repeat updates the row it already has and moves it to the
// end, where the newest try belongs.
//
// Words are normalized through the server's own `normalizeWord`, because a list
// that disagreed with the server about what two words are would show a ruling
// on one row landing on another.
// ---------------------------------------------------------------------------

import { normalizeWord, scoreLettersWord } from '@countdown/shared';

/** Offered and waiting, or ruled on. */
export type WordOfferStatus = 'pending' | 'valid' | 'invalid';

export interface WordOffer {
  /** Normalized exactly as the server normalizes it. */
  word: string;
  status: WordOfferStatus;
  /** What it would score. Null until the server has ruled. */
  score: number | null;
  /** True on the one row that is currently the player's answer. */
  isAnswer: boolean;
  /** The action it went out on, which is how its ruling finds it again. */
  actionId: string;
  /** Not offered on this device: rebuilt from the answer the server held. */
  restored: boolean;
}

/** One ruling, as `word-result` carries it. */
export interface WordRuling {
  word: string;
  valid: boolean;
  score: number;
  adopted: boolean;
  actionId: string;
}

/** The same string the server will compare against. Empty when there is none. */
export function offerKey(word: string): string {
  return normalizeWord(word);
}

/** The row for this word, whatever state it is in. */
export function findOffer(offers: WordOffer[], word: string): WordOffer | undefined {
  const key = offerKey(word);
  if (key === '') return undefined;
  return offers.find((offer) => offer.word === key);
}

/** The row that is currently the answer, or null while there is none. */
export function answerOffer(offers: WordOffer[]): WordOffer | null {
  return offers.find((offer) => offer.isAnswer) ?? null;
}

/**
 * A word on its way to the server.
 *
 * A word already on the list takes its own row back rather than opening a
 * second one, carrying the new action id so the new ruling finds it. Two things
 * are deliberately kept across that:
 *
 *   - `isAnswer`, because the server is still holding whatever it held a moment
 *     ago. Blanking the marker for the length of a round trip would tell the
 *     player their answer had been dropped by the very act of sending it again.
 *   - `score`, which is the answer row's, for the same reason.
 *
 * `restored` is not kept: a word this device has now offered itself is no
 * longer only the server's copy of it.
 */
export function addOffer(offers: WordOffer[], word: string, actionId: string): WordOffer[] {
  const key = offerKey(word);
  if (key === '') return offers;
  const existing = findOffer(offers, key);
  const row: WordOffer = {
    word: key,
    status: 'pending',
    score: existing?.score ?? null,
    isAnswer: existing?.isAnswer ?? false,
    actionId,
    restored: false,
  };
  if (existing === undefined) return [...offers, row];
  // Moved to the end, which is the top of the list on screen: the row the
  // player is waiting on is the row they should not have to scroll for.
  return [...offers.filter((offer) => offer !== existing), row];
}

/**
 * The server's ruling on one word.
 *
 * Matched on `actionId` first: a fast typist has two words in flight, and the
 * id is the only thing that cannot be ambiguous between them. The word is the
 * fallback for the one case the id cannot cover — a ruling that outlived the
 * row, which is a reload mid-flight — and a ruling that matches nothing at all
 * is appended rather than dropped, because it is still the truth about this
 * player's answer.
 *
 * The fallback also catches the earlier ruling on a word that has since been
 * offered again, whose row now carries the newer id. That settles the right
 * row with the right verdict — both rulings are about the same string — and
 * the socket delivers them in order, so the last one applied is the newest.
 */
export function settleOffer(offers: WordOffer[], ruling: WordRuling): WordOffer[] {
  const key = offerKey(ruling.word);
  const found =
    offers.find((offer) => offer.actionId === ruling.actionId) ??
    offers.find((offer) => offer.word === key);

  const settled: WordOffer = {
    word: key,
    status: ruling.valid ? 'valid' : 'invalid',
    score: ruling.score,
    // Adoption is the server's word, not a length comparison done here.
    isAnswer: ruling.adopted,
    actionId: ruling.actionId,
    restored: false,
  };

  const next =
    found === undefined
      ? [...offers, settled]
      : offers.map((offer) => (offer === found ? { ...settled, restored: offer.restored } : offer));

  // Exactly one row may claim to be the answer, or the screen would show two
  // words as the one going in — which is the whole failure this feature exists
  // to end.
  if (!ruling.adopted) return next;
  return next.map((offer) =>
    offer.word === key || !offer.isAnswer ? offer : { ...offer, isAnswer: false },
  );
}

/**
 * The answer the server says it is holding, put back into an empty list.
 *
 * This is the reload case. The words tried before it are gone — they were only
 * ever here — but the answer itself was never this device's to lose, so it
 * comes back as a row that says where it came from.
 */
export function restoreAnswer(offers: WordOffer[], word: string): WordOffer[] {
  const key = offerKey(word);
  if (key === '' || findOffer(offers, key) !== undefined) return offers;
  return [
    ...offers.map((offer) => (offer.isAnswer ? { ...offer, isAnswer: false } : offer)),
    {
      word: key,
      status: 'valid',
      // The shared rule rather than the length, so a restored nine reads as the
      // eighteen it is worth.
      score: scoreLettersWord(key, true),
      isAnswer: true,
      actionId: `restored:${key}`,
      restored: true,
    },
  ];
}
